import Vue from "vue";
import Vuex from "vuex";
import router from "../router";
import axios from "axios";
import { DEFAULT_API_BASE } from "@/utils/apiBase";
import { isTrustedApiUrl } from "@/utils/apiRegionAllowlist";
import { resetSessionExpiry } from "@/utils/sessionExpiry";
import { sessionEndSigninRedirect } from "@/router/authRedirect";
import {
  STAFF_ROLES,
  INTERNAL_ROLES,
  MANAGER_PLUS_ROLES,
  ADMIN_PLUS_ROLES,
} from "@/constants/roles";

const BASE_URL = "http://localhost:3000/";

import createPersistedState from "vuex-persistedstate";
import { crmPersistReducer } from "./crmPersist";
import createMutationsSharer from "vuex-shared-mutations";
import websocketSubscriptions from "./modules/websocket_subscriptions";
import crm from "./modules/crm";
import tasks from "./modules/tasks";
import notifications from "./modules/notifications";
import incomingCalls from "./modules/incomingCalls";
import network from "./modules/network";
import appVersion from "./modules/appVersion";
import deviceLock from "./modules/deviceLock";
import { deviceLockPlugin } from "@/utils/deviceLock";
import { isPersistenceFrozen } from "@/utils/deviceTabs";
import Cookies from "js-cookie";
import { paywallView } from "@/helpers/billingPaywall"; // FT-070
import agentMarkerLoader from "@/components/agents/agentMarkerLoader"; // FT-184
import { setS3BaseProvider } from "@/utils/s3PublicUrl";
// import sessionManager from "./modules/session_manager";
// import SecureLS from "secure-ls";
// const ls = new SecureLS({ isCompression: false });
Vue.use(Vuex);

// Мутации, которые вкладки пересылают друг другу (vuex-shared-mutations).
// persistedstate пишет весь state при любой мутации любой вкладки: срез, который меняется
// вместе с общей мутацией, тоже должен быть общим — иначе соседняя вкладка затрёт его
// своим устаревшим (setUserSettings идёт парой с setCurrentUser в Profile.vue).
export const SHARED_MUTATIONS = [
  "refresh",
  "setCurrentUser",
  "unsetCurrentUser",
  "setUserSettings",
  "show_active_bookings",
  "show_ride_bookings",
  "servicesCheckTrue",
  "servicesCheckFalse",
  "setSearchInBookings",
  "setsortDateInBookings",
  "setFinanceMode",
  "addTeletypeNewMessage",
  "resetTeletypeMessagesCount",
];

// Vuex strict mode — только в сборке development: локальный dev-сервер и стенды e2e
// (`vue-cli-service build --mode development`, локально и в CI). Запись в state вне
// мутации — правка объекта из геттера на месте, sort() по массиву стора, v-model в
// общий со стором объект — бросает в синхронном глубоком вотчере, ошибка уходит в
// console.error, а e2e-фикстура (tests/e2e/fixtures/vuex-strict-guard.ts) роняет на ней
// тест. В production и staging выключено: глубокий вотчер на весь state дорог.
export function vuexStrictFor(nodeEnv) {
  return nodeEnv === "development";
}

export const store = new Vuex.Store({
  strict: vuexStrictFor(process.env.NODE_ENV),
  state() {
    return {
      packageVersion: process.env.VUE_APP_VERSION || "0",
      currentUser: {},
      user: {},
      user_settings: {},
      auth_token: null,
      company: {
        company: {
          mileage_limit: 0,
          mileage_limit_cost: 0,
        }
      },
      main_company: {
        balance: 0,
        payer_state: 0,
        currency: "EUR",
      },
      doc_template_url: "https://web.rentprog.ru/doc_example.docx",
      entity_doc_template_url:
        "https://web.rentprog.ru/entity_doc_example.docx",
      invoice_template_url: "https://web.rentprog.ru/invoice_example.docx",
      prolong_template_url: "https://web.rentprog.ru/prolong_example.docx",
      login_users: [],
      csrf: null,
      login_time: null,
      todos: [],
      drawer: false,
      show_active_bookings: true,
      show_ride_bookings: true,
      signedIn: false,
      snackbar: false,
      // servicesCheck: false,
      searchInBookings: null,
      sortDateInBookings: null,
      sortStateInBookings: null,
      new_booking: false,
      show_banner: true,
      show_store_kits: false,
      bannerRemoveDemoContent: false,
      tour_next_step: false,
      car_tab: 'bookings', // bookings, maintenance, store, integrations, taxi, starline
      whatsapp_search_client: null,
      finance_mode: false,
      teletype_messages_count: 0,
      teletype_messages: [],
      teletype_lastMessage: null,
      teletype_dialogs: [],
      teletype_dialog_messages: {},
      teletype_active_dialog: null,
      teletype_loading: false,
      company_profile_tab: 0,
      has_new_ticket: false,
      region: null,
      api_url: null,
      // FT-018 STEP-08b: full cable_urls hash из login response (per CTR-02).
      // Consumer (CableReconnect mixin) выбирает по cable_urls[region]
      // при flip региона — derived url'у нельзя верить (он один-единственный),
      // нужен hash для translation.
      cable_urls: null,
      // FT-007: база публичных ссылок бакета по регионам (storage_urls из ответа входа) — для s3PublicUrl.
      storage_urls: null,
      maintenanceMode: false,
    };
  },
  getters: {
    // !!!!!
    getAuthToken(state) {
      // state.auth_token пуст/строка "null" в окне между F5 и гидрацией vuex-persist
      // (мутация, ставящая токен, ещё не вызвана) → откатываемся на долгоживущий
      // localStorage "auth_token", который переживает refresh. Иначе запросы через
      // этот геттер (/company в mounted и appearance_user, Counts и т.д.) уходят без
      // токена → 401 → main.js handler делает unsetCurrentUser → вышибает на /signin.
      // localStorage чистится в unsetCurrentUser вместе с state.auth_token → logout корректен.
      return state.auth_token && state.auth_token !== "null"
        ? state.auth_token
        : localStorage.getItem("auth_token");
    },
    getUserEmail(state) {
      return state.user ? state.user.email : null;
    },
    getUserID(state) {
      return state.user ? state.user.id : null;
    },
    isLoggedIn(state) {
      // Источник истины — долгоживущий localStorage "auth_token", который переживает F5.
      // state.auth_token в окне между refresh и гидрацией vuex-persist может быть пуст/"null"
      // (мутация-сеттер ещё не вызвана). Раньше геттер смотрел ТОЛЬКО на state → в этот момент
      // компоненты с `if (isLoggedIn) {...} else { unsetCurrentUser }` в created() (Counts и др.)
      // уходили в else и вышибали сессию (hydration-race). Берём токен из стора, а при его
      // отсутствии — из localStorage. unsetCurrentUser чистит оба → реальный logout корректен.
      const token =
        state.auth_token && state.auth_token !== JSON.stringify(null)
          ? state.auth_token
          : localStorage.getItem("auth_token");
      const loggedOut = token == null || token == JSON.stringify(null);
      return !loggedOut;
    },
    // !!!!
    appVersion: (state) => {
      return state.packageVersion;
    },
    isAdmin(state) {
      return state.currentUser.role === "admin";
    },
    isManager(state) {
      return state.currentUser.role === "manager";
    },
    isSuperAdmin(state) {
      return state.currentUser.role === "superadmin";
    },
    isPartner(state) {
      return state.currentUser.role === "partner";
    },
    isGuest(state) {
      return state.currentUser.role === "guest";
    },
    isAgent(state) {
      return state.currentUser.role === "agent";
    },
    // Зеркало Api::V1::TasksController::STAFF_ROLES. Allowlist, а не «не guest и
    // не partner»: новая не-staff роль на бэкенде иначе молча получит доступ.
    // Список — в src/constants/roles.js, там же причины по каждой роли.
    isStaff(state) {
      return STAFF_ROLES.includes(state.currentUser.role);
    },
    // Сотрудники + guest: смотрят на компанию изнутри. См. constants/roles.js.
    isInternal(state) {
      return INTERNAL_ROLES.includes(state.currentUser.role);
    },
    // Ярус управления компанией — без рядового user.
    isManagerPlus(state) {
      return MANAGER_PLUS_ROLES.includes(state.currentUser.role);
    },
    // Владелец компании и админ, без manager'а.
    isAdminPlus(state) {
      return ADMIN_PLUS_ROLES.includes(state.currentUser.role);
    },
    currentUserId(state) {
      return state.currentUser && state.currentUser.id;
    },
    getDrawer(state) {
      return state.drawer;
    },
    getShowActiveBookings(state) {
      return state.show_active_bookings;
    },
    getShowRideBookings(state) {
      return state.show_ride_bookings;
    },
    getCurrentUser(state) {
      return state.currentUser;
    },
    getCompany(state) {
      return state.company;
    },
    getMainCompany(state) {
      return state.main_company;
    },
    // FT-070 (REQ-04): derive paywall-конфиг из billing_summary (FT-069 read-контракт)
    // + валюты компании. Уплощает вложенный dunning_summary под чистый маппер paywallView.
    // billing_summary отсутствует (старый payload / shadow) → tier none (инертно, CON-01).
    getPaywall(state) {
      const mc = state.main_company || {};
      const summary = mc.billing_summary || {};
      const dunning = summary.dunning_summary || {};
      return paywallView({
        paywall_mode: summary.paywall_mode,
        paywall_reason: summary.paywall_reason,
        enforced: summary.enforced,
        dunning_state: dunning.state,
        next_retry_at: dunning.next_retry_at,
        currency: mc.currency,
        autopay_available: summary.autopay_available, // 2026-09-17: честные CTA, не прокси по валюте
      });
    },
    getCompanyId(state) {
      return state.company && state.company.company ? state.company.company.id : null;
    },
    getDocUrl(state) {
      return state.doc_template_url;
    },
    getEntityDocUrl(state) {
      return state.entity_doc_template_url;
    },
    getInvoiceUrl(state) {
      return state.invoice_template_url;
    },
    getProlongTemplateUrl(state) {
      return state.prolong_template_url;
    },
    getServicesCheck(state) {
      return state.servicesCheck;
    },
    getCSRF(state) {
      return state.csrf;
    },
    getSignedIn(state) {
      return state.signedIn;
    },
    getLoginUsers(state) {
      return state.login_users;
    },
    getLoginTime(state) {
      return state.login_time;
    },
    getSearchInBookings(state) {
      return state.searchInBookings;
    },
    getSortDateInBookings(state) {
      return state.sortDateInBookings;
    },
    getSortStateInBookings(state) {
      return state.sortStateInBookings;
    },
    getNewBookingState(state) {
      return state.new_booking;
    },
    getShowBanner(state) {
      return state.show_banner;
    },
    getStoreKits(state) {
      return state.show_store_kits;
    },
    getBannerRemoveDemoContent(state) {
      return state.bannerRemoveDemoContent;
    },
    getTourNextStep(state) {
      return state.tour_next_step;
    },
    getCarTab(state) {
      return state.car_tab;
    },
    getUserSettings(state) {
      return state.user_settings;
    },
    getWhatsappSearchClient(state) {
      return state.whatsapp_search_client;
    },
    getFinanceMode(state) {
      return state.finance_mode;
    },
    getTeletypeMessagesCount(state) {
      return state.teletype_messages_count;
    },
    getTeletypeMessages(state) {
      return state.teletype_messages;
    },
    getTeletypeLastMessage(state) {
      return state.teletype_lastMessage;
    },
    getTeletypeDialogs(state) {
      return state.teletype_dialogs;
    },
    getTeletypeActiveDialog(state) {
      return state.teletype_active_dialog;
    },
    getTeletypeDialogMessages: state => dialogId => {
      return state.teletype_dialog_messages[dialogId] || [];
    },
    getTeletypeLoading(state) {
      return state.teletype_loading;
    },
    getCompanyProfileTab(state) {
      return state.company_profile_tab;
    },
    getHasNewTicket(state) {
      return state.has_new_ticket;
    },
    getRegion(state) {
      return state.region;
    },
    getApiUrl(state) {
      // FT-018: pre-login / fallback chain.
      // 1) Development mode → ВСЕГДА localhost из .env.development (защита от
      //    production region URL override через setRegionInfo).
      // 2) state.api_url (after login response) → 3) build-time ENV →
      // 4) legacy hardcoded fallback (NODE_ENV-aware).
      // Гарантирует non-null значение — упрощает callers (не нужен
      // `getApiUrl || "https://rentprog.net"` workaround).
      if (process.env.NODE_ENV === "development") {
        // Хост берём из origin страницы: в браузере это localhost, при
        // live-reload на устройстве — LAN-IP мака. API всегда на том же
        // хосте, порт 3000 (см. utils/apiBase.js).
        return process.env.VUE_APP_API_URL || `http://${window.location.hostname}:3000`;
      }
      // Гард инцидента 2026-08-26: vuex-persist восстанавливает api_url БЕЗ
      // мутации, так что отравленный чужим окружением персист (стендовая
      // сборка, живший до фикса замок DEC-06) попадает сюда напрямую. Адрес
      // вне allowlist (utils/apiRegionAllowlist.js) молча игнорируется в
      // пользу build-time дефолта — без console.error: геттер зовётся на
      // каждый запрос, шумная ветка живёт в bootstrap (main.js).
      if (state.api_url && isTrustedApiUrl(state.api_url, DEFAULT_API_BASE)) return state.api_url;
      if (process.env.VUE_APP_API_URL) return process.env.VUE_APP_API_URL;
      return "https://rentprog.net";
    },
    getMaintenanceMode(state) {
      return state.maintenanceMode;
    },
  },
  mutations: {
    // FT-191: тот же сотрудник разблокировал общий компьютер в другой вкладке — новый токен
    // уже лежит в localStorage, эта вкладка берёт его без перезагрузки (страница остаётся).
    ADOPT_TOKEN_LOCAL(state) {
      let token = null;
      try {
        token = localStorage.getItem("auth_token");
      } catch (e) {
        token = null;
      }
      if (!token) return;
      resetSessionExpiry(); // новое поколение: запоздавший 401 старого токена не блокирует снова
      state.auth_token = token;
      axios.defaults.headers.common["Authorization"] = token;
    },
    // FT-191: токен стирается только в этой вкладке и только как токен — без перехода на
    // форму входа (страница остаётся под экраном блокировки). Не входит в createMutationsSharer:
    // вкладки синхронизирует протокол deviceTabs.
    CLEAR_TOKEN_LOCAL(state) {
      state.auth_token = null;
      delete axios.defaults.headers.common["Authorization"];
      try {
        localStorage.removeItem("auth_token");
      } catch (e) {
        // хранилище недоступно — токен на сервере уже недействителен
      }
      Cookies.remove("auth_token"); // вход кладёт токен и в cookie — её тоже читают из DevTools
    },
    setUserInfo(state, data) {
      // FT-136: успешный вход гасит плашку и возвращает право объяснить следующее
      // истечение. Сброс привязан к мутации, а не к компоненту: точек входа шесть —
      // пять веток Signin.vue и вход после регистрации в Signup.vue.
      resetSessionExpiry();
      state.user = data.data.user;
      // Поддержка обоих вариантов регистра для authorization header
      const authToken = data.headers.authorization || data.headers.Authorization || data.headers['authorization'];
      
      // Токен должен храниться С префиксом "Bearer " для корректной работы JWT
      state.auth_token = authToken;
      axios.defaults.headers.common["Authorization"] = authToken;
      localStorage.setItem("auth_token", authToken);
      state.currentUser = data.data.user;
      state.signedIn = true;
      state.login_time = Date.now();

      // Сохраняем регион если есть в ответе
      const prevRegion = state.region;
      const prevApiUrl = state.api_url;
      if (data.data.region) {
        state.region = data.data.region;
      }
      // Development mode: local backend возвращает production region api_urls
      // (rentprog.net/.pro), что после login переключило бы axios на production.
      // Pin к dev-хосту (env override или хост страницы: браузер → localhost,
      // live-reload на девайсе → LAN-IP мака) для safe локального тестирования.
      // Mirror of setRegionInfo dev-guard. Region для UI logic сохраняется.
      if (process.env.NODE_ENV === "development") {
        const devUrl = process.env.VUE_APP_API_URL || `http://${window.location.hostname}:3000`;
        state.api_url = devUrl;
        axios.defaults.baseURL = devUrl;
      } else if (data.data.api_urls && data.data.region) {
        const url = data.data.api_urls[data.data.region];
        // Гард инцидента 2026-08-26: применяем только адреса из allowlist
        // (origin сборки + VUE_APP_TRUSTED_API_ORIGINS — там появятся EU/RU
        // канонические API при разделении). Отклонение логируется громко:
        // молчание здесь спрятало бы забытый origin нового региона на приёмке.
        if (url && isTrustedApiUrl(url, DEFAULT_API_BASE)) {
          state.api_url = url;
          axios.defaults.baseURL = url;
        } else if (url) {
          // eslint-disable-next-line no-console
          console.error("[region] setUserInfo: rejected api_url outside the trusted allowlist", {
            url,
            defaultBase: DEFAULT_API_BASE,
          });
        }
      }
      // FT-018 STEP-08b: full cable_urls hash для CableReconnect mixin (CTR-02).
      if (data.data.cable_urls) {
        state.cable_urls = data.data.cable_urls;
      }
      // FT-007: база ссылок на файлы бакета ячейки.
      if (data.data.storage_urls) {
        state.storage_urls = data.data.storage_urls;
      }
      if (process.env.NODE_ENV !== "production") {
        console.log("[region] setUserInfo", {
          incomingRegion: data.data.region,
          incomingApiUrls: data.data.api_urls,
          incomingCableUrls: data.data.cable_urls,
          prevRegion,
          prevApiUrl,
          nextRegion: state.region,
          nextApiUrl: state.api_url,
          nextCableUrls: state.cable_urls,
          axiosBaseURL: axios.defaults.baseURL,
        });
      }
    },
    setMaintenanceMode(state, value) {
      state.maintenanceMode = value;
    },
    setRegionInfo(state, { region, api_urls, cable_urls, storage_urls }) {
      const prevRegion = state.region;
      const prevApiUrl = state.api_url;
      if (region) {
        state.region = region;
      }
      // Development mode: backend (local) возвращает production region api_urls
      // (rentprog.net/.pro), что после login переключило бы axios на production.
      // Pin к dev-хосту (env override или хост страницы: браузер → localhost,
      // live-reload на девайсе → LAN-IP мака) для safe локальной разработки.
      // Region для UI logic сохраняется.
      if (process.env.NODE_ENV === "development") {
        const devUrl = process.env.VUE_APP_API_URL || `http://${window.location.hostname}:3000`;
        state.api_url = devUrl;
        axios.defaults.baseURL = devUrl;
      } else if (api_urls && region) {
        const url = api_urls[region];
        // Гард инцидента 2026-08-26 — зеркально setUserInfo, см. комментарий там.
        if (url && isTrustedApiUrl(url, DEFAULT_API_BASE)) {
          state.api_url = url;
          axios.defaults.baseURL = url;
        } else if (url) {
          // eslint-disable-next-line no-console
          console.error("[region] setRegionInfo: rejected api_url outside the trusted allowlist", {
            url,
            defaultBase: DEFAULT_API_BASE,
          });
        }
      }
      // FT-018 STEP-08b: full cable_urls hash (CTR-02).
      if (cable_urls) {
        state.cable_urls = cable_urls;
      }
      if (storage_urls) {
        state.storage_urls = storage_urls; // FT-007
      }
      if (process.env.NODE_ENV !== "production") {
        console.log("[region] setRegionInfo", {
          incomingRegion: region,
          incomingApiUrls: api_urls,
          incomingCableUrls: cable_urls,
          prevRegion,
          prevApiUrl,
          nextRegion: state.region,
          nextApiUrl: state.api_url,
          nextCableUrls: state.cable_urls,
          axiosBaseURL: axios.defaults.baseURL,
        });
      }
    },
    setUserSettings(state, data) {
      state.user_settings = data;
    },
    setUserInfoFromToken(state, data) {
      state.user = data.data.user;
      state.auth_token = localStorage.getItem("auth_token");
    },
    setAuthToken(state, token) {
      state.auth_token = token;
    },
    // !!!!
    setCurrentUser(state, currentUser) {
      state.currentUser = currentUser;
      state.signedIn = true;
      // state.csrf = csrf;
      state.login_time = Date.now();
    },
    unsetCurrentUser(state, options = {}) {
      const redirectOptions = options && typeof options === "object" ? options : {};
      if (process.env.NODE_ENV !== "production") {
        console.log("[region] unsetCurrentUser — resetting", {
          prevRegion: state.region,
          prevApiUrl: state.api_url,
        });
      }
      // Сначала очищаем всё состояние
      state.user = {};
      state.currentUser = {};
      state.auth_token = null;
      state.signedIn = false;
      state.csrf = null;
      state.company = {};
      state.main_company = {};
      state.login_time = null;
      state.region = null;
      state.api_url = null;
      state.cable_urls = null; // FT-018 STEP-08b
      state.storage_urls = null; // FT-007
      state.todos = [];
      state.drawer = false;
      state.show_active_bookings = true;
      state.show_ride_bookings = false;
      state.searchInBookings = null;
      state.sortDateInBookings = null;
      state.login_users = [];
      state.packageVersion = 0;
      
      // Очищаем localStorage и axios headers
      localStorage.removeItem("auth_token");
      axios.defaults.headers.common["Authorization"] = null;
      // FT-018 post-review: reset axios baseURL к default чтобы post-logout
      // requests из Signin не уходили на stale региональный URL предыдущей
      // session. Logout response (sessions_controller.rb#log_out_success) намеренно
      // не возвращает region_payload — Signin.vue далее заново вычислит регион
      // через checkUserPasskeys / request_otp.
      // Trade-off: in-flight requests до logout sent с прежним baseURL остаются
      // на старом URL (resolve там же); они получат 401 (Authorization=null
      // уже cleared строкой выше) → main.js 401-handler re-вызывает
      // unsetCurrentUser idempotently. Acceptable для S1 single-region.
      // Real-fix candidate (S2+): AbortController на in-flight requests при logout.
      axios.defaults.baseURL = DEFAULT_API_BASE;
      
      // lastUsedEmail НЕ удаляем - пусть остается для автозаполнения при следующем входе

      // FT-056 INV-01: сбрасываем CRM conversion-sessions при logout
      if (state.crm) state.crm.conversions = [];

      // FT-184: кэш меток «через агента» — данные прежнего пользователя и его группы
      agentMarkerLoader.reset();

      // FT-076: сбрасываем модуль задач (ленивый кэш исполнителей привязан к филиалу)
      if (state.tasks) {
        state.tasks.items = [];
        state.tasks.meta = {};
        state.tasks.users = [];
        state.tasks.usersLoaded = false;
      }

      // Безопасный переход на страницу входа (replace не добавляет в историю)
      if (redirectOptions.navigate !== false) {
        router.replace(sessionEndSigninRedirect(router.currentRoute, redirectOptions.returnTo)).catch(() => false);
      }
    },
    refresh(state, csrf) {
      state.signedIn = true;
      state.csrf = csrf;
    },
    setCompany(state, company) {
      state.company = company;
    },
    setMainCompany(state, main_company) {
      state.main_company = main_company;
    },
    setHasNewTicket(state, has_new_ticket) {
      state.has_new_ticket = has_new_ticket;
    },
    setDocUrl(state, url) {
      state.doc_template_url = url;
    },
    setEntityDocUrl(state, url) {
      state.entity_doc_template_url = url;
    },
    setInvoiceUrl(state, url) {
      state.invoice_template_url = url;
    },
    setProlongTemplateUrl(state, url) {
      state.prolong_template_url = url;
    },
    setLoginUsers(state, user) {
      let arr_users = [];
      // Текущие
      state.login_users.forEach((item) => {
        arr_users.push(item);
      });

      // Чистим массив от дублей
      if (arr_users.findIndex((item) => item.id == user.id) >= 0) {
        function removeDuplicates(myArr, prop) {
          return myArr.filter((obj, pos, arr) => {
            return arr.map((mapObj) => mapObj[prop]).indexOf(obj[prop]) === pos;
          });
        }
        arr_users = removeDuplicates(arr_users, "id");
      }

      if (user.login == true) {
        let index = arr_users.findIndex((item) => item.id == user.id);
        // Добавляем нового
        if (index == -1) {
          arr_users.push(user);
        } else {
          arr_users.splice(index, 1, { id: user.id, login: true });
        }
      }

      // console.log("user", user);
      if (user.login == false) {
        // console.log("user login false", user);
        let index = arr_users.findIndex((item) => item.id == user.id);
        if (index >= 0) {
          arr_users.splice(index, 1, { id: user.id, login: false });
        }
        // let index = arr_users.findIndex(item => item.id == user.id)
        // if (index >= 0) {
        //   let finded_user = arr_users.find(item => item.id == user.id)
        //   arr_users = arr_users.filter(item => item.id != finded_user.id)
        //   console.log("finded_user", finded_user)
        //   console.log("arr_users", arr_users)
        // }
      }

      // console.log(arr_users);

      return (state.login_users = arr_users);
    },
    show_active_bookings(state) {
      return (state.show_active_bookings = !state.show_active_bookings);
    },
    show_ride_bookings(state, value) {
      return (state.show_ride_bookings = value);
    },
    setDrawer(state, value) {
      return state.drawer = value;
    },
    servicesCheckTrue(state) {
      return (state.servicesCheck = true);
    },
    servicesCheckFalse(state) {
      return (state.servicesCheck = false);
    },
    setSearchInBookings(state, search) {
      return (state.searchInBookings = search);
    },
    setsortDateInBookings(state, range) {
      return (state.sortDateInBookings = range);
    },
    setSortStateInBookings(state, value) {
      return (state.sortStateInBookings = value);
    },
    setNewBooking(state, new_booking_exists) {
      state.new_booking = new_booking_exists;
    },
    setShowBanner(state, close_banner) {
      state.show_banner = close_banner;
    },
    setStoreKits(state) {
      state.show_store_kits = !state.show_store_kits;
    },
    // Явное значение, не переключатель: удаление при уже снятом флаге (другой браузер,
    // удаление со страницы профиля) включало баннер обратно поверх demo_content из стора.
    setBannerRemoveDemoContent(state, value) {
      state.bannerRemoveDemoContent = !!value;
    },
    setTourNextStep(state, force) {
      state.tourNextStep = force;
    },
    setCarTab(state, tab) {
      state.car_tab = tab;
    },
    setWhatsappSearchClient(state, search) {
      state.whatsapp_search_client = search;
    },
    setFinanceMode(state, mode) {
      state.finance_mode = mode;
    },
    addTeletypeNewMessage(state, payload) {
      if (payload && payload.message) {
        state.teletype_messages_count++;

        const newMessage = payload.message;
        state.teletype_messages.unshift(newMessage);

        if (state.teletype_messages.length > 50) {
          state.teletype_messages = state.teletype_messages.slice(0, 50);
        }

        state.teletype_lastMessage = newMessage;

        // Если сообщение относится к активному диалогу, добавляем его туда
        if (newMessage.dialogId && state.teletype_dialog_messages[newMessage.dialogId]) {
          this.commit('addTeletypeDialogMessage', {
            dialogId: newMessage.dialogId,
            message: newMessage
          });
        }
      }
    },
    resetTeletypeMessagesCount(state) {
      state.teletype_messages_count = 0;
    },
    incrementTeletypeMessagesCount(state) {
      state.teletype_messages_count++;
    },
    markTeletypeMessageAsRead(state, messageId) {
      const messageIndex = state.teletype_messages.findIndex(msg => msg.id === messageId);
      if (messageIndex >= 0) {
        const updatedMessage = { ...state.teletype_messages[messageIndex], read: true };
        state.teletype_messages.splice(messageIndex, 1, updatedMessage);
      }
    },
    removeTeletypeMessage(state, messageId) {
      state.teletype_messages = state.teletype_messages.filter(msg => msg.id !== messageId);
    },
    setTeletypeDialogs(state, dialogs) {
      state.teletype_dialogs = dialogs.map(dialog => ({
        ...dialog,
        hasUnreadMessages: dialog.unreadCount > 0,
        hasActiveBooking: false // можно расширить проверкой бронирований
      }));
    },
    addTeletypeDialog(state, dialog) {
      const index = state.teletype_dialogs.findIndex(d => d.id === dialog.id);
      if (index >= 0) {
        // Обновление существующего диалога
        state.teletype_dialogs.splice(index, 1, {
          ...dialog,
          hasUnreadMessages: dialog.unreadCount > 0,
          hasActiveBooking: false
        });
      } else {
        // Добавление нового диалога
        state.teletype_dialogs.push({
          ...dialog,
          hasUnreadMessages: dialog.unreadCount > 0,
          hasActiveBooking: false
        });
      }
    },
    setTeletypeActiveDialog(state, dialog) {
      state.teletype_active_dialog = dialog;
    },
    setTeletypeLoading(state, isLoading) {
      state.teletype_loading = isLoading;
    },
    setTeletypeDialogMessages(state, { dialogId, messages }) {
      // Используем Vue.set для реактивного обновления
      Vue.set(state.teletype_dialog_messages, dialogId, messages);
    },
    addTeletypeDialogMessage(state, { dialogId, message }) {
      if (!state.teletype_dialog_messages[dialogId]) {
        Vue.set(state.teletype_dialog_messages, dialogId, []);
      }

      // Проверка на дубликаты
      const existingIndex = state.teletype_dialog_messages[dialogId].findIndex(m => m.id === message.id);
      if (existingIndex >= 0) {
        // Обновляем существующее сообщение
        state.teletype_dialog_messages[dialogId].splice(existingIndex, 1, message);
      } else {
        // Добавляем новое сообщение
        state.teletype_dialog_messages[dialogId].push(message);

        // Сортируем сообщения по времени (от старых к новым)
        state.teletype_dialog_messages[dialogId].sort((a, b) => {
          const timeA = a.createdAt?.date ? new Date(a.createdAt.date).getTime() :
            (a.timestamp ? (typeof a.timestamp === 'number' ? a.timestamp * 1000 : new Date(a.timestamp).getTime()) : 0);
          const timeB = b.createdAt?.date ? new Date(b.createdAt.date).getTime() :
            (b.timestamp ? (typeof b.timestamp === 'number' ? b.timestamp * 1000 : new Date(b.timestamp).getTime()) : 0);
          // Меняем порядок сортировки
          return timeB - timeA;
        });
      }
    },
    updateTeletypeMessageStatus(state, { dialogId, messageId, status }) {
      if (state.teletype_dialog_messages[dialogId]) {
        const messageIndex = state.teletype_dialog_messages[dialogId].findIndex(m => m.id === messageId);
        if (messageIndex >= 0) {
          const updatedMessage = { ...state.teletype_dialog_messages[dialogId][messageIndex], status };
          state.teletype_dialog_messages[dialogId].splice(messageIndex, 1, updatedMessage);
        }
      }
    },
    updateTeletypeDialog(state, { dialogId, lastMessage }) {
      const dialogIndex = state.teletype_dialogs.findIndex(d => d.id === dialogId);
      if (dialogIndex >= 0) {
        const dialog = { ...state.teletype_dialogs[dialogIndex] };

        // Обновляем последнее сообщение и время
        dialog.lastMessage = lastMessage;
        if (lastMessage.createdAt && lastMessage.createdAt.date) {
          dialog.lastMessageAt = lastMessage.createdAt;
        }

        // Если сообщение от клиента и это не активный диалог, отмечаем его как непрочитанный
        if ((lastMessage.isItClient || lastMessage.isOutgoing === false) &&
          (!state.teletype_active_dialog || state.teletype_active_dialog.id !== dialogId)) {
          dialog.hasUnreadMessages = true;
          dialog.unreadCount = dialog.unreadCount ? dialog.unreadCount + 1 : 1;
        }

        // Обновляем диалог в списке
        Vue.set(state.teletype_dialogs, dialogIndex, dialog);

        // Перемещаем диалог в начало списка
        if (dialogIndex > 0) {
          const updatedDialog = state.teletype_dialogs.splice(dialogIndex, 1)[0];
          state.teletype_dialogs.unshift(updatedDialog);
        }
      }
    },
    updateTeletypeDialogMessageStatus(state, { dialogId, messageId, status }) {
      const dialogIndex = state.teletype_dialogs.findIndex(d => d.id === dialogId);
      if (dialogIndex >= 0) {
        const dialog = { ...state.teletype_dialogs[dialogIndex] };

        // Если последнее сообщение в диалоге - это сообщение, статус которого изменился
        if (dialog.lastMessage && dialog.lastMessage.id === messageId) {
          // Обновляем статус последнего сообщения
          dialog.lastMessage.status = status;

          // Обновляем диалог в списке
          Vue.set(state.teletype_dialogs, dialogIndex, dialog);
        }
      }
    },
    markTeletypeDialogAsRead(state, dialogId) {
      const dialogIndex = state.teletype_dialogs.findIndex(d => d.id === dialogId);
      if (dialogIndex >= 0) {
        const dialog = { ...state.teletype_dialogs[dialogIndex] };
        dialog.hasUnreadMessages = false;
        dialog.unreadCount = 0;

        // Обновляем диалог в списке
        Vue.set(state.teletype_dialogs, dialogIndex, dialog);
      }
    },
    addTeletypeDialogFromMessage(state, payload) {
      const { message } = payload;

      // Проверяем, существует ли диалог
      const existingDialogIndex = state.teletype_dialogs.findIndex(d => d.id === message.dialogId);

      if (existingDialogIndex === -1) {
        // Создаем новый диалог на основе сообщения
        const newDialog = {
          id: message.dialogId,
          client: message.client || null,
          title: message.client ? message.client.name : (message.channel ? message.channel.name : 'Новый диалог'),
          channel: message.channel || {},
          lastMessage: message,
          lastMessageAt: message.createdAt,
          hasUnreadMessages: true,
          unreadCount: 1
        };

        // Добавляем диалог в начало списка
        state.teletype_dialogs.unshift(newDialog);

        // Инициализируем список сообщений для нового диалога
        Vue.set(state.teletype_dialog_messages, message.dialogId, [message]);
      }
    },
    updateTeletypeMessage(state, { dialogId, messageId, updates }) {
      if (state.teletype_dialog_messages[dialogId]) {
        const messageIndex = state.teletype_dialog_messages[dialogId].findIndex(m => m.id === messageId);
        if (messageIndex >= 0) {
          const updatedMessage = {
            ...state.teletype_dialog_messages[dialogId][messageIndex],
            ...updates
          };
          state.teletype_dialog_messages[dialogId].splice(messageIndex, 1, updatedMessage);
        }
      }
    },
    setTeletypeDialogUnread(state, { dialogId, unreadCount }) {
      const dialogIndex = state.teletype_dialogs.findIndex(d => d.id === dialogId);
      if (dialogIndex >= 0) {
        const dialog = { ...state.teletype_dialogs[dialogIndex] };
        dialog.hasUnreadMessages = true;
        dialog.unreadCount = unreadCount;

        // Обновляем диалог в списке
        Vue.set(state.teletype_dialogs, dialogIndex, dialog);
      }
    },
    setCompanyProfileTab(state, tab) {
      state.company_profile_tab = tab;
    },
  },
  actions: {
    loginUserWithToken({ commit }, payload) {
      const config = {
        headers: {
          Authorization: payload.auth_token,
        },
      };
      new Promise((resolve, reject) => {
        axios
          .get(`${BASE_URL}member-data`, config)
          .then((response) => {
            commit("setUserInfoFromToken", response);
            resolve(response);
          })
          .catch((error) => {
            reject(error);
          });
      });
    },
    loadTeletypeDialogs({ commit, state }, { page = 1, pageSize = 50 } = {}) {
      commit('setTeletypeLoading', true);

      return new Promise((resolve, reject) => {
        axios.get('/api/v1/integrations/teletype/dialogs', {
          headers: {
            Authorization: state.auth_token
          },
          params: {
            page: page,
            page_size: pageSize
          }
        })
          .then(response => {
            if (response.data && response.data.data) {
              if (response.data.data.items) {
                commit('setTeletypeDialogs', response.data.data.items);
              }

              // Возвращаем информацию о пагинации
              resolve({
                totalPages: response.data.data.totalPages || 1,
                currentPage: response.data.data.currentPage || 1,
                items: response.data.data.items || []
              });
            } else {
              commit('setTeletypeDialogs', []);
              resolve({ totalPages: 1, currentPage: 1, items: [] });
            }
          })
          .catch(error => {
            console.error('Ошибка при загрузке диалогов:', error);
            reject(error);
          })
          .finally(() => {
            commit('setTeletypeLoading', false);
          });
      });
    },
    loadTeletypeDialogMessages({ commit, state }, dialogId) {
      if (!dialogId) return Promise.reject(new Error('Не указан ID диалога'));

      commit('setTeletypeLoading', true);

      return new Promise((resolve, reject) => {
        axios.get('/api/v1/integrations/teletype/messages', {
          headers: {
            Authorization: state.auth_token
          },
          params: {
            dialog_id: dialogId,
            page_size: 100
          }
        })
          .then(response => {
            if (response.data && response.data.data && response.data.data.items) {
              const messages = response.data.data.items;
              commit('setTeletypeDialogMessages', { dialogId, messages });
              resolve(messages);
            } else {
              commit('setTeletypeDialogMessages', { dialogId, messages: [] });
              resolve([]);
            }
          })
          .catch(error => {
            console.error('Ошибка при загрузке сообщений:', error);
            reject(error);
          })
          .finally(() => {
            commit('setTeletypeLoading', false);
          });
      });
    },
    sendTeletypeMessage({ commit, state }, { dialogId, text, file }) {
      if (!dialogId) return Promise.reject(new Error('Не указан ID диалога'));

      commit('setTeletypeLoading', true);

      const formData = new FormData();
      formData.append('dialog_id', dialogId);

      if (text && text.trim() !== '') {
        formData.append('text', text);
      }

      if (file) {
        formData.append('file', file);
      }

      return new Promise((resolve, reject) => {
        axios.post('/api/v1/integrations/teletype/create_message', formData, {
          headers: {
            'Authorization': state.auth_token,
            'Content-Type': 'multipart/form-data'
          }
        })
          .then(response => {
            if (response.data && response.data.success) {
              // Создаем временное сообщение для отображения
              const sentMessage = {
                id: response.data.data && response.data.data.ids ? response.data.data.ids[0] : Date.now().toString(),
                dialogId: dialogId,
                type: file ? 'file' : 'text',
                text: text,
                sender: 'user',
                timestamp: Math.floor(Date.now() / 1000),
                isOutgoing: true,
                status: 10 // Статус "отправляется"
              };

              commit('addTeletypeDialogMessage', { dialogId, message: sentMessage });
              resolve(sentMessage);
            } else {
              reject(new Error('Неуспешный ответ от сервера'));
            }
          })
          .catch(error => {
            console.error('Ошибка при отправке сообщения:', error);
            reject(error);
          })
          .finally(() => {
            commit('setTeletypeLoading', false);
          });
      });
    },
    loadMessagesForDialog(dialog) {
      // ... существующий код ...

      // Устанавливаем активный диалог в store
      this.$store.commit('setTeletypeActiveDialog', dialog);

      // Отмечаем диалог как прочитанный
      if (dialog.hasUnreadMessages) {
        this.markDialogAsRead(dialog.id);
      }

      // ... остальной код ...
    },
  },
  modules: {
    // sessionManager,
    websocketSubscriptions,
    crm,
    tasks,
    notifications,
    incomingCalls,
    network,
    appVersion,
    deviceLock,
  },
  plugins: [
    deviceLockPlugin, // FT-191: режим общего компьютера по ответу любого входа (setUserInfo)
    createPersistedState({
      // Исключаем websocketSubscriptions из персистентного state
      // WebSocket подписки должны пересоздаваться при каждой загрузке страницы
      reducer: crmPersistReducer,
      // FT-191: после смены сотрудника на общем компьютере вкладка не пишет vuex — иначе
      // состояние прежнего сотрудника вернётся в хранилище после очистки.
      setState: (key, state, storage) => {
        if (isPersistenceFrozen()) return;
        storage.setItem(key, JSON.stringify(state));
      },
    }),
    // Сохраняет мутации в разных вкладках
    createMutationsSharer({
      // FT-191: замороженная вкладка (смена сотрудника) мутации не рассылает.
      predicate: (mutation) => !isPersistenceFrozen() && SHARED_MUTATIONS.indexOf(mutation.type) !== -1,
      // storage: {
      //   getItem: (key) => ls.get(key),
      //   setItem: (key, value) => ls.set(key, value),
      //   removeItem: (key) => ls.remove(key)
      // }
    }),
  ],
});

// FT-007: s3PublicUrl читает базу бакета текущего региона из стора в момент склейки.
setS3BaseProvider(() => store.state.storage_urls && store.state.storage_urls[store.state.region]);
